/**
 * wild_gait — the PURE motion rules of the wild creatures (WildsModule; the physics follow-up's item 3, 2026-10-05;
 * audit-feel G3/G14: "the creatures you fight are rigid statues gliding along the ground").
 *
 * Two halves, both deterministic and allocation-free:
 *
 *  THE GLIDE — where the body is drawn. The server moves a chasing creature `speed × dt` per wildsTick (~2 s); the old
 *  client chased each new row with `position.lerp(target, dt·2.2)`, which starts every 2 s leg at 2.2× the beast's pace
 *  and decays: a surge-and-coast no gait could ride. `glideRow` plans each leg to ARRIVE when the next row is due: the
 *  gap to the new row over the observed row interval (an EMA), never above the species' speed × `GLIDE.paceMax` unless
 *  the body has fallen more than `GLIDE.lagS` of travel behind; `stepGlide` walks that steady speed and stops ON the
 *  row (no overshoot). A beast that keeps chasing therefore walks at its pace; one that stops reaches its last row in one
 *  interval and stands. A jump past `GLIDE.snapM` (a respawn at the den) snaps.
 *
 *  THE GAIT — how the body moves while it goes. `stepWildGait` turns the drawn speed and the telegraph into the four
 *  numbers the vertex shader reads (lib/wild_models.ts): a cycle phase advanced by speed ÷ stride (an idle cycle while
 *  it stands — breathing, a tail's sway, a drifter's bob), the leg swing (walk → run by speed), the neck, and the
 *  windup / strike. Every phase multiplier in the shader is an INTEGER, so the phase can wrap at 2π·256 seamlessly.
 *
 * The species table: quadrupeds walk the herds' lateral-sequence gait (the ridge stalker, the duneback brute, the mire
 * lurker); the coiled CRAG WYRM ripples its coils and sends a travelling wave up its raised neck; the SPORE DRONE drifts
 * above the ground, its cap pulsing and its tentacles trailing; the GLASS SHRIKE strides on two legs with a pigeon's head
 * bob and beats its folded wings at a run. Looked at: render-glb-views of all six served LOD0 files (every head +Z).
 */

export type WildBody = 'quadruped' | 'serpent' | 'drifter' | 'strider';

export interface WildGaitSpec {
  body: WildBody;
  /** Stride at a walk (m): the cycle rate is speed ÷ stride (a run lengthens the stride by `runStretch`). */
  stride: number;
  /** At this drawn speed (m/s) the beast is fully at a run (a trot, a lope, a beating run); half of it starts the blend. */
  runAt: number;
  /** The swing amplitude (rad for legs; an intensity for the wyrm's waves and the drifter's tentacles) at a walk / a run. */
  swingWalk: number;
  swingRun: number;
  /** The cycle rate (Hz) while it stands — breathing, a tail's sway, the drifter's bob, the wyrm's coil. */
  idleHz: number;
  /** The neck channel (rad, + = head down) standing, at the end of the windup, at the strike (quadrupeds). */
  neckIdle: number;
  neckWindup: number;
  neckLunge: number;
}

export const WILD_GAIT: Readonly<Record<string, WildGaitSpec>> = {
  // a 2.6 m raptor-legged hunter: long strides, a quick lope
  ridge_stalker: { body: 'quadruped', stride: 1.6, runAt: 3.0, swingWalk: 0.36, swingRun: 0.62, idleHz: 0.35, neckIdle: 0, neckWindup: 0.32, neckLunge: -0.28 },
  // a 3.8 m armoured heavyweight: short, heavy swings
  duneback_brute: { body: 'quadruped', stride: 1.9, runAt: 2.2, swingWalk: 0.22, swingRun: 0.4, idleHz: 0.25, neckIdle: 0, neckWindup: 0.22, neckLunge: -0.16 },
  // a 2.4 m sprawling ambusher: quick, short strides
  mire_lurker: { body: 'quadruped', stride: 1.1, runAt: 3.6, swingWalk: 0.4, swingRun: 0.66, idleHz: 0.3, neckIdle: 0, neckWindup: 0.2, neckLunge: -0.32 },
  // a 4.4 m coiled serpent: the "stride" is the wave's travel per cycle
  crag_wyrm: { body: 'serpent', stride: 2.2, runAt: 2.0, swingWalk: 0.55, swingRun: 1.0, idleHz: 0.18, neckIdle: 0, neckWindup: 0, neckLunge: 0 },
  // a drifting fungal swarm: it never touches the ground
  spore_drone: { body: 'drifter', stride: 2.6, runAt: 4.0, swingWalk: 0.45, swingRun: 0.9, idleHz: 0.42, neckIdle: 0, neckWindup: 0, neckLunge: 0 },
  // a 1.9 m crystalline raptor on two legs
  glass_shrike: { body: 'strider', stride: 0.9, runAt: 4.0, swingWalk: 0.34, swingRun: 0.55, idleHz: 0.4, neckIdle: 0, neckWindup: 0, neckLunge: 0 },
};

/** The fallback for an unknown species: a quadruped walk. */
export const WILD_GAIT_DEFAULT: WildGaitSpec = WILD_GAIT.ridge_stalker;
export const wildGaitFor = (species: string): WildGaitSpec => WILD_GAIT[species] ?? WILD_GAIT_DEFAULT;

/** The phase wraps here: a multiple of 2π, so every integer harmonic stays continuous across the wrap. */
export const PHASE_WRAP = Math.PI * 2 * 256;

export const GAIT = {
  /** The drawn speed is low-passed with this rate (1/s) before it drives the cycle (a 2 s row cadence must not pulse it). */
  speedRate: 6,
  /** Below this drawn speed (m/s) the beast is standing. */
  standM: 0.05,
  /** The swing ramps in over the first metres per second. */
  swingRampM: 0.6,
  /** A run lengthens the stride by this fraction. */
  runStretch: 0.35,
  /** Ease rates (1/s): swing, run blend, neck. */
  swingRate: 5,
  runRate: 3,
  neckRate: 6,
  /** The strike's signal decays over ~1/lungeDecay s (the telegraph's lunge itself is 140 ms). */
  lungeDecay: 3,
} as const;

export interface WildGaitState {
  phase: number;
  phasePrev: number;
  /** The leg swing (rad) / wave intensity, eased. */
  swing: number;
  /** The neck (rad, + = head down), eased. */
  neck: number;
  /** The smoothed drawn speed (m/s). */
  speed: number;
  /** 0 a walk … 1 a run, eased. */
  run: number;
  /** The telegraph: 0..1 (the windup's ease, as combat_feel.windupPose leans the body). */
  windup: number;
  /** The strike: 1 the moment the blow lands, decaying. */
  lunge: number;
}

/** A fresh gait; `seed` desynchronises the cycles of a pack (no two beasts step in time). */
export function makeWildGait(seed: number): WildGaitState {
  const s = Math.abs(Math.sin(seed * 12.9898) * 43758.5453) % 1;
  const phase = s * Math.PI * 2;
  return { phase, phasePrev: phase, swing: 0, neck: 0, speed: 0, run: 0, windup: 0, lunge: 0 };
}

export interface WildGaitInput {
  /** The drawn (glide) speed this frame, m/s. */
  speed: number;
  /** The windup's ease 0..1 (0 when no telegraph). */
  windup: number;
  /** True on the frames the strike lands (the telegraph's lunge window). */
  lungeNow: boolean;
  /** Reeling from a heavy blow: the legs stop. */
  staggered: boolean;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** One frame of the gait. Mutates `g`; allocates nothing. */
export function stepWildGait(g: WildGaitState, spec: WildGaitSpec, input: WildGaitInput, dt: number): void {
  if (!(dt > 0)) { g.phasePrev = g.phase; return; }
  const sp = Number.isFinite(input.speed) && input.speed > 0 ? input.speed : 0;
  g.speed += (sp - g.speed) * Math.min(1, dt * GAIT.speedRate);
  const runTarget = clamp01((g.speed - spec.runAt * 0.5) / (spec.runAt * 0.5));
  g.run += (runTarget - g.run) * Math.min(1, dt * GAIT.runRate);
  const moving = g.speed > GAIT.standM;
  const hz = moving ? Math.max(spec.idleHz * 0.5, g.speed / (spec.stride * (1 + GAIT.runStretch * g.run))) : spec.idleHz;
  g.phasePrev = g.phase;
  g.phase += hz * Math.PI * 2 * dt;
  if (g.phase >= PHASE_WRAP) { g.phase -= PHASE_WRAP; g.phasePrev -= PHASE_WRAP; }
  const swingTarget = !moving || input.staggered ? 0
    : (spec.swingWalk + (spec.swingRun - spec.swingWalk) * g.run) * Math.min(1, g.speed / GAIT.swingRampM);
  g.swing += (swingTarget - g.swing) * Math.min(1, dt * GAIT.swingRate);
  g.windup = clamp01(Number.isFinite(input.windup) ? input.windup : 0);
  g.lunge = input.lungeNow ? 1 : Math.max(0, g.lunge - dt * GAIT.lungeDecay);
  const neckTarget = spec.neckIdle + (spec.neckWindup - spec.neckIdle) * g.windup;
  const neckGoal = neckTarget + (spec.neckLunge - neckTarget) * g.lunge;
  // the telegraph drives the neck directly (a tell must read on the frame it fills); otherwise it eases home
  g.neck = g.windup > 0 || g.lunge > 0 ? neckGoal : g.neck + (neckGoal - g.neck) * Math.min(1, dt * GAIT.neckRate);
}

// ─── the glide ────────────────────────────────────────────────────────────────

export const GLIDE = {
  /** A row this far from the drawn body (m) is a respawn / teleport: snap. */
  snapM: 30,
  /** The body never lags more than this many seconds of travel behind the newest row (it speeds up past the cap). */
  lagS: 2.4,
  /** A planned leg never exceeds the species' speed by more than this factor (a late row is not a sprint). */
  paceMax: 1.25,
  /** Intervals between rows are judged inside this band (s); before any is seen, the wildsTick cadence. */
  minIntervalS: 0.25,
  maxIntervalS: 4,
  defaultIntervalS: 2,
  /** The interval EMA's weight for a new observation. */
  intervalGain: 0.3,
} as const;

export interface WildGlide {
  /** The drawn position. */
  x: number;
  z: number;
  /** The newest row. */
  tx: number;
  tz: number;
  /** The planned speed of the current leg (m/s): the gap over the expected interval. */
  pace: number;
  /** The observed interval between rows (s, an EMA). */
  interval: number;
  /** When the newest row landed (s, the caller's clock); NaN before the first. */
  rowAt: number;
  /** The drawn velocity this frame (m/s). */
  vx: number;
  vz: number;
}

export function makeGlide(x: number, z: number): WildGlide {
  return { x, z, tx: x, tz: z, pace: 0, interval: GLIDE.defaultIntervalS, rowAt: NaN, vx: 0, vz: 0 };
}

/** A new row landed at (x, z) at `nowS`: plan the leg to arrive when the next row is due. `speciesSpeed` (m/s) caps it. */
export function glideRow(g: WildGlide, x: number, z: number, nowS: number, speciesSpeed: number): void {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return;
  if (Number.isFinite(g.rowAt) && Number.isFinite(nowS)) {
    const iv = Math.min(GLIDE.maxIntervalS, Math.max(GLIDE.minIntervalS, nowS - g.rowAt));
    g.interval += (iv - g.interval) * GLIDE.intervalGain;
  }
  g.rowAt = nowS;
  g.tx = x; g.tz = z;
  const gap = Math.hypot(x - g.x, z - g.z);
  if (gap > GLIDE.snapM) { g.x = x; g.z = z; g.vx = 0; g.vz = 0; g.pace = 0; return; }
  g.pace = Math.min(Math.max(0, speciesSpeed) * GLIDE.paceMax, gap / Math.max(GLIDE.minIntervalS, g.interval));
}

/** Walk the drawn body toward the newest row for `dt` s; returns the drawn speed (m/s). Never passes the row. */
export function stepGlide(g: WildGlide, dt: number): number {
  const gx = g.tx - g.x, gz = g.tz - g.z;
  const gap = Math.hypot(gx, gz);
  if (!(dt > 0) || gap < 1e-4) { g.vx = 0; g.vz = 0; return 0; }
  const speed = Math.max(g.pace, gap / GLIDE.lagS);
  const step = Math.min(gap, speed * dt);
  g.x += (gx / gap) * step;
  g.z += (gz / gap) * step;
  g.vx = (gx / gap) * (step / dt);
  g.vz = (gz / gap) * (step / dt);
  return step / dt;
}

/** The shortest signed turn from `a` to `b` (rad). */
export function turnDelta(a: number, b: number): number {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2; else if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** Turn `yaw` toward `target` by at most `rate × dt` (rad). */
export function turnToward(yaw: number, target: number, rate: number, dt: number): number {
  const d = turnDelta(yaw, target);
  const m = Math.max(0, rate * dt);
  return yaw + (d > m ? m : d < -m ? -m : d);
}

/** Heading of a velocity in the creatures' frame (head +Z): rotation.y = atan2(vx, vz). */
export const headingOf = (vx: number, vz: number): number => Math.atan2(vx, vz);
